import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const SITE_KEY = "fsyl_site_token";
const ADMIN_KEY = "fsyl_admin_token";
const VAULT_KEY = "fsyl_vault_token";

export const getSiteToken = () => localStorage.getItem(SITE_KEY);
export const setSiteToken = (t) => localStorage.setItem(SITE_KEY, t);
export const clearSiteToken = () => localStorage.removeItem(SITE_KEY);

export const getAdminToken = () => localStorage.getItem(ADMIN_KEY);
export const setAdminToken = (t) => localStorage.setItem(ADMIN_KEY, t);
export const clearAdminToken = () => localStorage.removeItem(ADMIN_KEY);

export const getVaultToken = () => localStorage.getItem(VAULT_KEY);
export const setVaultToken = (t) => localStorage.setItem(VAULT_KEY, t);
export const clearVaultToken = () => localStorage.removeItem(VAULT_KEY);

const makeClient = (tokenFn) => {
  const c = axios.create({ baseURL: API });
  c.interceptors.request.use((config) => {
    const t = tokenFn();
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
  });
  return c;
};

const siteClient = makeClient(() => getAdminToken() || getSiteToken());
const adminClient = makeClient(getAdminToken);
const vaultClient = makeClient(() => getAdminToken() || getVaultToken());

export const api = {
  verifyPasscode: (passcode) => axios.post(`${API}/auth/passcode`, { passcode }).then(r => r.data),
  verifyVaultPasscode: (passcode) => axios.post(`${API}/auth/vault`, { passcode }).then(r => r.data),
  adminLogin: (email, password) => axios.post(`${API}/auth/admin/login`, { email, password }).then(r => r.data),
  me: () => adminClient.get(`/auth/me`).then(r => r.data),

  // site/admin
  listSections: (scope) => siteClient.get(`/sections`, { params: scope ? { scope } : {} }).then(r => r.data),
  createSection: (body) => adminClient.post(`/sections`, body).then(r => r.data),
  deleteSection: (id) => adminClient.delete(`/sections/${id}`).then(r => r.data),
  listMedia: (opts = {}) => siteClient.get(`/media`, { params: opts }).then(r => r.data),

  // vault
  listVaultSections: () => vaultClient.get(`/sections`, { params: { scope: "vault" } }).then(r => r.data),
  listVaultMedia: (opts = {}) => vaultClient.get(`/media`, { params: { vault: "true", ...opts } }).then(r => r.data),

  // admin
  uploadMedia: (formData, opts = {}) =>
    adminClient.post(`/media`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000, // 3 min — large videos can take a while
      ...opts,
    }).then(r => r.data),
  patchMedia: (id, body) => adminClient.patch(`/media/${id}`, body).then(r => r.data),
  deleteMedia: (id) => adminClient.delete(`/media/${id}`).then(r => r.data),
  reorderMedia: (orders) => adminClient.post(`/media/reorder`, { orders }).then(r => r.data),

  updatePasscode: (new_passcode) => adminClient.put(`/settings/passcode`, { new_passcode }).then(r => r.data),
  updateVaultPasscode: (new_passcode) => adminClient.put(`/settings/vault-passcode`, { new_passcode }).then(r => r.data),

  // Theme song (public GET for the passcode page)
  getTheme: () => axios.get(`${API}/settings/theme`).then(r => r.data),
  uploadTheme: (formData) =>
    adminClient.post(`/settings/theme/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 180000,
    }).then(r => r.data),
  toggleTheme: (enabled) => adminClient.patch(`/settings/theme`, { enabled }).then(r => r.data),
  deleteTheme: () => adminClient.delete(`/settings/theme`).then(r => r.data),
};

export const themeAudioUrl = () => `${API}/settings/theme/file`;

// Returns a media file URL embedding the most relevant token for the scope.
// scope: "site" | "vault" — defaults to "site"
export const fileUrl = (mediaId, scope = "site") => {
  const t = getAdminToken() || (scope === "vault" ? getVaultToken() : getSiteToken()) || "";
  return `${API}/media/${mediaId}/file?token=${encodeURIComponent(t)}`;
};

// Small thumbnail URL for grid views (only for images; videos fall back to full file)
export const thumbUrl = (mediaId, scope = "site", size = 480) => {
  const t = getAdminToken() || (scope === "vault" ? getVaultToken() : getSiteToken()) || "";
  return `${API}/media/${mediaId}/thumb?size=${size}&token=${encodeURIComponent(t)}`;
};

export const formatApiError = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
};
